import React, { useState } from "react";
import { Briefcase, MapPin, Clock, ArrowRight, Calendar } from "lucide-react";
import Button from "../components/Button";

const Career = () => {
  const jobs = [
    {
      id:1,
      title: "Python Developer Intern",
      department: "",
      location: "Chennai",
      type: "Unpaid Internship",
      description:
        "We are looking for a motivated Python Developer Intern to join our backend development team for a 3-month internship. This internship will provide hands-on experience in backend development, API development, databases, and data processing.",
      skills: [
        "Basic knowledge of Python",
        "Basic understanding of Django",
        "Familiarity with Django ORM",
        "Basic knowledge of PostgreSQL",
        "Understanding of REST APIs",
        "Basic knowledge of Django REST Framework",
        "Familiarity with Pandas is an advantage",
        "Basic understanding of Microservices is an advantage",
        "Knowledge of Postman is an advantage",
      ],
      responsibilities: [
        "Assist in developing backend applications using Python and Django.",
        "Assist in developing and testing REST APIs.",
        "Work with PostgreSQL databases and Django ORM.",
        "Support data processing and integration activities.",
        "Test APIs using Postman.",
        "Debug and resolve basic application issues.",
        "Work with senior developers and other team members.",
        "Write clean and maintainable code.",
      ],
      eligibility: [
        "Students or recent graduates with a background in Computer Science, IT, or related fields.",
        "Candidates with basic knowledge of Python and web development.",
        "Willingness to learn and work on real-time projects.",
        "Good communication and problem-solving skills.",
      ],
      mail: "hr@keelis.com",
    },
    {
      id:2,
      title: "Node.js Developer",
      department: "",
      location: "Chennai",
      type: "Full-Time",
      experience: "4+ Years",
      description:
        "We are looking for an experienced Node.js Developer to join our backend development team. The candidate will be responsible for developing backend applications, REST APIs, database integrations, and scalable services.",
      skills: [
        "Strong experience in Node.js and JavaScript",
        "Good knowledge of Express.js",
        "Experience in REST API development",
        "Strong knowledge of PostgreSQL / SQL",
        "Experience with API integration and Postman",
        "Familiarity with Git",
        "Knowledge of Microservices Architecture is an advantage",
      ],
      responsibilities: [
        "Develop and maintain backend applications using Node.js.",
        "Design and develop REST APIs.",
        "Work with PostgreSQL databases and optimize queries.",
        "Implement business logic, authentication, validation, and error handling.",
        "Integrate internal and third-party APIs.",
        "Debug, troubleshoot, and optimize applications.",
        "Write clean, reusable, and maintainable code.",
        "Collaborate with frontend and QA teams.",
      ],
      eligibility: [
        "4+ years of hands-on experience in Node.js backend development.",
        "Good problem-solving and communication skills.",
        "Ability to work independently and as part of a team.",
      ],
      mail: "hr@keelis.com",
    },
    {
      id:3,
      title: "SailPoint IAM Developer",
      department: "",
      location: "Chennai / Hybrid",
      type: "Contract",
      experience: "1+ Years",
      contract_duration: "1 Year",
      joining: "Immediate / Short Notice Preferred",
      description:
        "We are looking for a SailPoint IAM Developer with 1+ years of hands-on experience to join our team on a 1-year contract. The candidate will be involved in SailPoint implementation, configuration, integration, and support activities.",
      skills: [
        "1+ years of hands-on experience with SailPoint IAM.",
        "Good understanding of Identity and Access Management (IAM) concepts.",
        "Knowledge of SailPoint IdentityIQ and/or IdentityNow.",
        "Familiarity with Active Directory, LDAP, SQL, and REST APIs.",
        "Basic knowledge of Java, JavaScript, and XML is an advantage.",
      ],
      responsibilities: [
        "Configure and support SailPoint IAM solutions.",
        "Handle user provisioning, de-provisioning, and identity lifecycle management.",
        "Work with SailPoint IdentityIQ / IdentityNow.",
        "Configure workflows, rules, policies, and connectors.",
        "Integrate SailPoint with Active Directory, LDAP, databases, and applications.",
        "Troubleshoot and resolve IAM-related issues.",
        "Support testing, deployment, and technical documentation.",
      ],
      mail: "talenthunt@keelis.com",
    },
  ];
  const [expandedJob, setExpandedJob] = useState(null);
  const [loadingJobId, setLoadingJobId] = useState(null);
  const handleEmail = (jobId, email) => {
    setLoadingJobId(jobId);

    window.location.href = `mailto:${email}`;

    setTimeout(() => {
      setLoadingJobId(null);
    }, 5000);
  };
  return (
    <div className="pt-24 min-h-screen bg-navy/5">
      <section className="py-10 text-center">
        <div className="container-custom">
          <div className="inline-block mb-4 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20">
            <span className="text-primary font-semibold text-sm tracking-wide uppercase">
              Careers
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-navy mb-6">
            Do the Best Work of <br className="hidden md:block" />
            <span className="text-primary">Your Life</span>
          </h1>
          <p className="text-lg md:text-xl text-navy/70 max-w-3xl mx-auto leading-relaxed mb-10">
            We're building a culture of high agency, radical candor, and
            technical excellence. Join us in solving some of the hardest
            engineering challenges for fast-growing companies.
          </p>
          <a href="#open-positions">
            <Button variant="primary" className="px-8 py-4 text-lg">
              View Open Roles
            </Button>
          </a>
        </div>
      </section>
      <section id="open-positions" className="py-10 bg-navy/5">
        <div className="container-custom">
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-primary mb-2">
                Opportunities
              </p>

              <h2 className="text-3xl md:text-4xl font-bold text-navy">
                Open Positions
              </h2>
            </div>

            <span className="w-fit bg-primary/10 text-primary font-bold px-4 py-2 rounded-lg text-sm">
              {jobs.length} Open Roles
            </span>
          </div>

          {/* Job Cards */}
          <div className="grid grid-cols-1 md:grid-cols-1 gap-8 cursor-pointer">
            {jobs.map((job, index) => {
              const isExpanded = expandedJob === index;

              return (
                <div
                  key={index}
                  className={`group relative flex flex-col justify-between overflow-hidden rounded-xl border
                             border-navy/10 bg-white p-7 px-4 md:px-7 pb-4 shadow-sm transition-all duration-300 
                             hover:-translate-y-1 hover:shadow-xl ${isExpanded ? "shadow-xl" : ""}`}
                  onClick={() => setExpandedJob(isExpanded ? null : index)}
                >
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-navy/5 transition-colors group-hover:bg-primary/10">
                        <Briefcase
                          size={20}
                          className="text-navy transition-colors group-hover:text-primary"
                        />
                      </div>

                      <span className="rounded-md border border-navy/10 px-3 py-1.5 text-xs font-semibold text-navy/60">
                        {job.type}
                      </span>
                    </div>

                    <h3 className="text-xl md:text-2xl font-bold leading-tight text-navy transition-colors group-hover:text-primary">
                      {job.title}
                    </h3>

                    <p className="mt-2 text-sm font-medium text-navy/50">
                      {job.department}
                    </p>

                    <p className="mt-2 text-sm">
                      {job.description}{" "}
                      <a className="text-sky-500 underline underline-offset-2 font-medium hover:opacity-80">
                        {!isExpanded ? "Read More..." : "Read Less..."}
                      </a>
                    </p>
                  </div>

                  {isExpanded && (
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <h4 className="mb-3 text-sm font-bold text-navy">
                          Skills Required
                        </h4>

                        <ul className="space-y-2">
                          {job.skills.map((skill, i) => (
                            <li
                              key={i}
                              className="flex items-start gap-2 text-sm text-navy/60 text-justify"
                            >
                              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-navy/40" />
                              <span>{skill}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h4 className="mb-3 text-sm font-bold text-navy">
                          Key Responsibilities
                        </h4>

                        <ul className="space-y-2">
                          {job.responsibilities.map((item, i) => (
                            <li
                              key={i}
                              className="flex items-start gap-2 text-sm text-navy/60 text-justify"
                            >
                              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-navy/40" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      {job.eligibility && (
                        <div>
                          <h4 className="mb-3 text-sm font-bold text-navy">
                            Eligibility
                          </h4>

                          <ul className="space-y-2">
                            {job.eligibility.map((item, i) => (
                              <li
                                key={i}
                                className="flex items-start gap-2 text-sm text-navy/60 text-justify"
                              >
                                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-navy/40" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Bottom Section */}
                  <div
                    className="mt-5 flex justify-between"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex flex-wrap items-center gap-3 md:gap-5 text-sm font-medium text-navy/60">
                      {job.experience && (
                        <>
                          <div className="flex items-center gap-1.5">
                            <Briefcase size={16} />
                            <span>
                              <b>Experience : </b>
                              {job.experience}
                            </span>
                          </div>
                          {job.contract_duration && (
                            <div className="flex items-center gap-1.5">
                              <Briefcase size={16} />
                              <span>
                                <b>Contract Duration : </b>
                                {job.contract_duration}
                              </span>
                            </div>
                          )}
                        </>
                      )}

                      <div className="flex items-center gap-1.5">
                        <MapPin size={16} />
                        <span>{job.location}</span>
                      </div>
                    </div>
                    <div className="px-4 py-1.5 h-10 rounded-full text-nowrap font-medium transition-all duration-300 transform hover:scale-105 active:scale-95 flex items-center justify-center bg-primary text-white shadow-lg hover:shadow-primary/30">
                      <a
                        href={`mailto:${job.mail}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEmail(job.id, job.mail);
                        }}
                        className="shrink-0 rounded-md border border-primary text-sm font-semibold transition-all duration-200 hover:bg-primary hover:text-white"
                      >
                        {loadingJobId === job.id ? "Opening..." : "Contact Us"}
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};
export default Career;
